import React, { useContext, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
// Context / Hooks
import { AlertNotificationContext } from "../contexts/AlertNotificationContext";
import useUser from "../hooks/useUser";
import { useItemPrice } from "../hooks/useItemPrice";
import { useVariantStock } from "../hooks/useVariantStock";
// New refactored hooks & components
import { useVariantCascade } from "./orderItem/hooks/useVariantCascade";
import { useOrderItemActions } from "./orderItem/hooks/useOrderItemActions";
import { VariantSelectors } from "./orderItem/VariantSelectors";
import { QuantityControl } from "./orderItem/QuantityControl";
import { StockChip } from "./orderItem/StockChip";
import { ProductImageAvatar } from "./ProductImageAvatar";
import { ComponentAuthorisation } from "./ComponentAuthorisation";
import { OverEntitlementDialog } from "./OverEntitlementDialog";
import { DraftEntitlementDialog } from "./DraftEntitlementDialog";
import { BasketQuantityDialog } from "./BasketQuantityDialog";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
// Extracted components / utils
import ItemPrice from "./orderItem/ItemPrice";
// legacy lead time utils now handled inside StockChip; keep if needed elsewhere

// A single product entry rendered as a stable card layout (Option D).
// Goals:
// - No layout shift when variant dimensions appear/disappear.
// - Consistent alignment across cards using a fixed grid template.
// - Preserve existing business logic (entitlement, SKU validation, price/stock caching).

export const OrderItemCard = ({
  item,
  itemLineKey,
  backendLineNo,
  basketLines,
  person,
  order,
  staffRequest,
  control,
  setValue,
  getFieldState,
  clearErrors,
  watch,
  handleSubmit,
  resetField,
  handleImageClick,
  singleColumn = false,
  orderType = "draft",
}) => {
  const queryClient = useQueryClient();
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const { user } = useUser();

  // entitlement & form field prefix
  let availalbeEntitlement = Number(item.entitlement) - Number(item.qtyIssued);
  const fieldPrefix = `items.${itemLineKey}`;

  // variant cascade hook (colour/size/fit + activeSku + lists + validity)
  const {
    selectedColour,
    selectedSize,
    selectedFit,
    sizes,
    fits,
    activeSku,
    validSKU,
  } = useVariantCascade({ item, control, fieldPrefix, watch, setValue });

  // quantity watcher
  const quantity =
    useWatch({ name: `${fieldPrefix}.quantity`, control, defaultValue: 0 }) ||
    0;

  const validationHelper = useMemo(
    () => ({
      colour: { required: "Colour is required" },
      size: { required: "Size is required" },
      fit: { required: "Fit is required" },
      quantity: {
        required: "Quantity is required",
        min: "Minimum 1",
        max: "Exceeds entitlement",
      },
    }),
    [],
  );
  // Order item actions (mutation + dialog) now encapsulated
  const {
    entitlementDialogOpen,
    setEntitlementDialogOpen,
    draftEntitlementDialogOpen,
    setDraftEntitlementDialogOpen,
    draftEntitlementMode,
    overEntitlementReason,
    setOverEntitlmentReason,
    dataToSave,
    setDataToSave,
    handleAddItem,
    handleDraftEntitlementConfirm,
    basketConflicts,
    handleBasketChoice,
    cancelBasketChoice,
    saveOrder,
  } = useOrderItemActions({
    item,
    itemLineKey,
    backendLineNo,
    basketLines,
    order,
    person,
    staffRequest,
    availalbeEntitlement,
    fieldPrefix,
    resetField,
    setAlertNotification,
    orderType,
    overEntitlementAction: user?.overEntitlementAction || "No Action",
    userType: user?.userType || "",
  });

  // pricing & stock queries (SKU-driven only)
  const price = useItemPrice(activeSku, !!activeSku);
  const variantStock = useVariantStock(order.no, activeSku, !!activeSku);
  const totalValue = price * quantity;

  // Quantity handlers
  const decrementQuantity = () => {
    clearErrors(`${fieldPrefix}.quantity`);
    const curr = Number(watch(`${fieldPrefix}.quantity`) || 0);
    if (curr <= 0) return;
    setValue(`${fieldPrefix}.quantity`, curr - 1, { shouldDirty: true });
  };
  const incrementQuantity = () => {
    clearErrors(`${fieldPrefix}.quantity`);
    const curr = Number(watch(`${fieldPrefix}.quantity`) || 0);
    setValue(`${fieldPrefix}.quantity`, curr + 1, { shouldDirty: true });
  };
  const autofillEntitlement = () => {
    if (availalbeEntitlement < 0) availalbeEntitlement = 0;
    setValue(`${fieldPrefix}.quantity`, availalbeEntitlement, {
      shouldDirty: true,
    });
  };

  // Visibility rules (extendable per-role)
  const showStock = !staffRequest; // future: refine based on permissions
  const showPricing = !staffRequest;
  const showPriceTotals = showPricing;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        width: "100%",
        flex: 1,
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: 0,
          pb: 1,
          "&:last-child": { pb: 1 },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Row 1: Media + (Title/Code/Entitlement) (no inputs) */}
        <Grid
          container
          columnSpacing={2}
          rowSpacing={1}
          wrap="wrap"
          sx={{
            alignItems: "flex-start",
          }}
        >
          <Grid
            size={{
              xs: 12,
              md: "auto",
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: "flex-start",
              }}
            >
              <IconButton
                onClick={() => handleImageClick(item.productCode)}
                sx={{ p: 0, alignSelf: "flex-start" }}
              >
                <ProductImageAvatar productCode={item.productCode} />
              </IconButton>
              <Stack spacing={0.25} sx={{ minWidth: 160, maxWidth: 320 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.productName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {item.productCode}
                  </Typography>
                  <ComponentAuthorisation
                    allowedRoles={["allowentitlement", "staffRequestCreate"]}
                  >
                    <Chip
                      size="small"
                      color="secondary"
                      variant="outlined"
                      label={`Entitlement: ${
                        availalbeEntitlement < 0 ? 0 : availalbeEntitlement
                      }`}
                    />
                  </ComponentAuthorisation>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          {/* Stock moved to footer metrics cluster */}
        </Grid>
        {/* Row 2: Variant inputs only */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
          }}
        >
          <VariantSelectors
            item={item}
            fieldPrefix={fieldPrefix}
            control={control}
            validationHelper={validationHelper}
            selectedColour={selectedColour}
            selectedSize={selectedSize}
            sizes={sizes}
            fits={fits}
            QuantitySlot={
              <QuantityControl
                control={control}
                fieldName={`${fieldPrefix}.quantity`}
                validSKU={validSKU}
                decrementQuantity={decrementQuantity}
                incrementQuantity={incrementQuantity}
                autofillEntitlement={autofillEntitlement}
                validationHelper={validationHelper}
              />
            }
            singleRow={singleColumn}
          />
        </Box>
      </CardContent>
      {/* Persistent footer: Stock | Price | Total | Add (Quantity moved above) */}
      <CardActions sx={{ pt: 0, px: 0, pb: 0, mt: "auto" }} disableSpacing>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            m: 0,
            // px: 1.5,
            py: 1,
            pb: 0,
            border: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 0,
            display: "grid",
            gap: 1,
            alignItems: "center",
            gridTemplateColumns: {
              xs: "1fr 1fr", // stock | price
              sm: showPriceTotals ? "auto 1fr auto" : "auto 1fr", // stock | (price+total) | add
            },
            gridAutoRows: "min-content",
          }}
        >
          {/* Stock */}
          <Box sx={{ minWidth: 0 }}>
            <StockChip
              showStock={showStock}
              variantStock={variantStock}
              activeSku={activeSku}
            />
          </Box>
          {/* Price & Total */}
          <Box
            sx={{
              justifySelf: { xs: "end", sm: "center" },
              minWidth: 0,
              display: showPriceTotals ? "block" : "none",
            }}
          >
            <ItemPrice
              price={price}
              totalValue={totalValue}
              showPricing={showPriceTotals}
            />
          </Box>
          {/* Add button */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", sm: "auto" },
              mt: { xs: 0.5, sm: 0 },
            }}
          >
            <Button
              size="small"
              fullWidth
              variant="contained"
              color="success"
              startIcon={<AddShoppingCartIcon />}
              onClick={handleSubmit(handleAddItem)}
              disabled={
                Number(quantity) === 0 ||
                !validSKU() ||
                (showPricing && price === 0)
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              Add
            </Button>
            {showPricing && price === 0 && validSKU() && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 0.5, display: "block", textAlign: "center" }}
              >
                Price error
              </Typography>
            )}
          </Box>
        </Paper>
      </CardActions>
      <OverEntitlementDialog
        open={entitlementDialogOpen}
        setOpen={setEntitlementDialogOpen}
        reason={overEntitlementReason}
        setReason={setOverEntitlmentReason}
        saveOrder={saveOrder}
        saveData={dataToSave}
        setDataToSave={setDataToSave}
      />
      <DraftEntitlementDialog
        open={draftEntitlementDialogOpen}
        setOpen={setDraftEntitlementDialogOpen}
        reason={overEntitlementReason}
        setReason={setOverEntitlmentReason}
        onConfirm={handleDraftEntitlementConfirm}
        mode={draftEntitlementMode}
      />
      <BasketQuantityDialog
        open={basketConflicts.length > 0}
        conflicts={basketConflicts}
        onChoose={handleBasketChoice}
        onCancel={cancelBasketChoice}
      />
    </Card>
  );
};

export default React.memo(OrderItemCard);
